import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState, EmptyState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE = 50;

const FILTERS = [
  { value: "alles", label: "Alles" },
  { value: "wijziging", label: "Wijzigingen" },
  { value: "login", label: "Inloggen" },
];

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
};

const who = (row) =>
  row.source === "publiek" ? "publieke melding" : row.actor_name || row.actor_email;

export const SettingsActivity = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("alles");

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    setLoadError("");
    try {
      const kind = filter === "alles" ? "" : `&kind=${filter}`;
      const { data } = await api.get(`/activity?limit=${PAGE}&offset=${offset}${kind}`);
      setRows((cur) => (offset === 0 ? data.items : [...cur, ...data.items]));
      setTotal(data.total);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Het logboek kon niet geladen worden."));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(0); }, [load]);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-body text-muted-foreground">
          Wat er is gewijzigd en wie er heeft ingelogd. Wat iemand alleen bekijkt komt hier niet in.
        </p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-full sm:w-44" data-testid="activity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loadError ? (
        <ErrorState testid="activity-load-error" message={loadError} onRetry={() => load(0)} />
      ) : !loading && rows.length === 0 ? (
        <EmptyState
          testid="activity-empty"
          title="Nog niets vastgelegd"
          purpose="Zodra iemand inlogt of iets aanpast, staat dat hier met tijd, naam en wat er gewijzigd is."
        />
      ) : (
        <>
          <Card>
            <div className="hidden sm:block">
              <Table data-testid="activity-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Wanneer</TableHead>
                    <TableHead className="w-[200px]">Wie</TableHead>
                    <TableHead>Wat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} data-testid={`activity-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-body tabular-nums text-muted-foreground">{when(r.at)}</TableCell>
                      <TableCell className="text-body text-foreground">{who(r)}</TableCell>
                      <TableCell>
                        <div className={`text-body ${r.kind === "login_geweigerd" ? "text-fout-tekst" : "text-foreground"}`}>
                          {r.summary}
                        </div>
                        {r.object_id && (
                          <div className="font-mono text-data text-muted-foreground">{r.object_id}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={3} className="py-8 text-center text-body text-muted-foreground">Laden...</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="divide-y divide-border sm:hidden" data-testid="activity-cardlist">
              {rows.map((r) => (
                <div key={r.id} data-testid={`activity-card-${r.id}`} className="space-y-0.5 p-3">
                  <div className={`text-body ${r.kind === "login_geweigerd" ? "text-fout-tekst" : "text-foreground"}`}>
                    {r.summary}
                  </div>
                  <div className="text-data text-muted-foreground">{[who(r), when(r.at)].filter(Boolean).join(" · ")}</div>
                  {r.object_id && <div className="font-mono text-data text-muted-foreground">{r.object_id}</div>}
                </div>
              ))}
              {loading && <div className="p-3 text-body text-muted-foreground">Laden...</div>}
            </div>
          </Card>

          {rows.length < total && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-data text-muted-foreground">{rows.length} van {total}</p>
              <Button
                variant="secondary"
                data-testid="activity-load-more"
                disabled={loading}
                onClick={() => load(rows.length)}
              >
                Meer laden
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
